import React, { useRef, useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { motion, AnimatePresence } from 'framer-motion';
import { useInView } from 'react-intersection-observer';
import { ExternalLink, Home, Scissors, Calendar, Menu, X, Share2 } from 'lucide-react';
import { Link } from 'react-router-dom';
import axios from 'axios';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const MediaReel = ({ item, index }) => {
  const { t } = useTranslation();
  const videoRef = useRef(null);
  const { ref, inView } = useInView({ threshold: 0.5 });

  useEffect(() => {
    if (videoRef.current) {
      if (inView) {
        videoRef.current.play().catch(() => {});
      } else {
        videoRef.current.pause();
      }
    }
  }, [inView]);

  const isVideo = item.media_type === 'VIDEO';
  const mediaSrc = item.media_url || item.thumbnail_url;

  return (
    <div ref={ref} className="feed-slide" data-testid={`reel-${index}`}>
      {isVideo ? (
        <video
          ref={videoRef}
          className="feed-video"
          src={mediaSrc}
          muted
          loop
          playsInline
          preload="metadata"
        />
      ) : (
        <img
          src={mediaSrc}
          alt={item.caption ? item.caption.substring(0, 80) : 'CasaPooch'}
          className="feed-video"
        />
      )}

      <div className="feed-gradient"></div>

      <div className="feed-overlay">
        <div className="feed-info">
          <motion.div
            initial={{ opacity: 0, y: 20 }}
            animate={inView ? { opacity: 1, y: 0 } : {}}
            transition={{ delay: 0.2 }}
          >
            <div className="flex items-center gap-3 mb-3">
              <div className="w-8 h-8 rounded-full bg-primary flex items-center justify-center">
                <span className="text-white font-bold text-xs">CP</span>
              </div>
              <span className="text-white font-medium text-sm">@lecasapooch</span>
            </div>
            {item.caption && (
              <p className="text-white text-xs mb-1 line-clamp-2">
                {item.caption.length > 120 ? item.caption.substring(0, 120) + '...' : item.caption}
              </p>
            )}
          </motion.div>
        </div>

        <div className="feed-actions">
          <motion.a
            whileTap={{ scale: 0.9 }}
            href={item.permalink}
            target="_blank"
            rel="noopener noreferrer"
            className="action-btn"
            aria-label={t('gallery.viewOnInstagram')}
          >
            <ExternalLink className="w-5 h-5" />
          </motion.a>
          <motion.button
            whileTap={{ scale: 0.9 }}
            className="action-btn"
            aria-label={t('gallery.share')}
            onClick={() => {
              if (navigator.share) {
                navigator.share({
                  title: 'CasaPooch',
                  text: item.caption || 'Check out CasaPooch!',
                  url: item.permalink || window.location.href
                });
              }
            }}
          >
            <Share2 className="w-5 h-5" />
          </motion.button>
        </div>
      </div>
    </div>
  );
};

function Gallery() {
  const { t } = useTranslation();
  const [media, setMedia] = useState([]);
  const [loading, setLoading] = useState(true);
  const [menuOpen, setMenuOpen] = useState(false);

  useEffect(() => {
    if (window.gtag) {
      window.gtag('event', 'page_view', {
        page_title: 'Gallery',
        page_path: window.location.pathname,
      });
    }

    axios.get(`${API_URL}/api/instagram/feed?limit=20`)
      .then(resp => {
        const items = (resp.data.media || []).filter(m => m.media_url || m.thumbnail_url);
        setMedia(items);
      })
      .catch(() => {})
      .finally(() => setLoading(false));
  }, []);

  const feedContent = loading ? (
    <div className="feed-slide flex items-center justify-center">
      <div className="animate-spin w-10 h-10 border-2 border-white border-t-transparent rounded-full"></div>
    </div>
  ) : media.length > 0 ? (
    media.map((item, index) => (
      <MediaReel key={item.id} item={item} index={index} />
    ))
  ) : (
    <div className="feed-slide flex items-center justify-center">
      <p className="text-white/60 text-lg">{t('gallery.noPosts')}</p>
    </div>
  );

  return (
    <div className="gallery-page" data-testid="gallery-page">
      {/* Floating Navigation Overlay */}
      <div className="gallery-nav" data-testid="gallery-nav">
        <Link to="/" className="gallery-nav-logo" data-testid="gallery-home-link">
          <img
            src="https://customer-assets.emergentagent.com/job_f2e1ed22-1a5e-4b46-a0b5-df4af0078423/artifacts/yu0958al_Emblem%20Brown.png"
            alt="CasaPooch"
            className="w-10 h-10"
          />
        </Link>

        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="gallery-nav-menu-btn"
          data-testid="gallery-menu-toggle"
        >
          {menuOpen ? <X className="w-6 h-6" /> : <Menu className="w-6 h-6" />}
        </button>
      </div>

      {/* Dropdown Menu */}
      <AnimatePresence>
        {menuOpen && (
          <motion.div
            initial={{ opacity: 0, y: -10 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -10 }}
            transition={{ duration: 0.2 }}
            className="gallery-dropdown"
            data-testid="gallery-dropdown"
          >
            <Link to="/" className="gallery-dropdown-item" onClick={() => setMenuOpen(false)}>
              <Home className="w-5 h-5" /> {t('nav.home')}
            </Link>
            <Link to="/services" className="gallery-dropdown-item" onClick={() => setMenuOpen(false)}>
              <Scissors className="w-5 h-5" /> {t('nav.services')}
            </Link>
            <Link to="/book" className="gallery-dropdown-item" onClick={() => setMenuOpen(false)}>
              <Calendar className="w-5 h-5" /> {t('nav.bookNow')}
            </Link>
            <Link to="/about" className="gallery-dropdown-item" onClick={() => setMenuOpen(false)}>
              {t('nav.about')}
            </Link>
            <Link to="/blog" className="gallery-dropdown-item" onClick={() => setMenuOpen(false)}>
              {t('nav.blog')}
            </Link>
            <Link to="/events" className="gallery-dropdown-item" onClick={() => setMenuOpen(false)}>
              {t('nav.events')}
            </Link>
            <Link to="/contact" className="gallery-dropdown-item" onClick={() => setMenuOpen(false)}>
              {t('nav.contact')}
            </Link>
          </motion.div>
        )}
      </AnimatePresence>

      {/* Mobile: Full-screen feed */}
      <div className="gallery-mobile-feed">
        <div className="feed-container">
          {feedContent}
        </div>
      </div>

      {/* Desktop: iPhone mockup */}
      <div className="gallery-desktop-wrapper">
        <div className="gallery-desktop-bg">
          <div className="gallery-desktop-title">
            <span className="gallery-desktop-label">@lecasapooch</span>
            <h2 className="gallery-desktop-heading">{t('gallery.liveFeed')}</h2>
          </div>

          {/* iPhone Frame */}
          <div className="iphone-frame">
            {/* Notch */}
            <div className="iphone-notch">
              <div className="iphone-notch-inner"></div>
            </div>
            {/* Screen */}
            <div className="iphone-screen">
              <div className="feed-container">
                {feedContent}
              </div>
            </div>
            {/* Home indicator */}
            <div className="iphone-home-bar">
              <div className="iphone-home-indicator"></div>
            </div>
          </div>

          <p className="gallery-desktop-hint">{t('gallery.hint')}</p>
        </div>
      </div>
    </div>
  );
}

export default Gallery;
